import React, { useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { MeshDistortMaterial, Html, useVideoTexture } from '@react-three/drei';
import * as THREE from 'three';
import projects from '../data/projects';
import { atom, useAtom } from 'jotai';

// Create atoms to store the active bubble information and hover state
export const activeBubbleAtom = atom(null);
export const isAnyBubbleHoveredAtom = atom(false);

const Bubble = ({ scrollEffect, position, size = 1, speed = 1, distortFactor = 0.4, cursor, videoPath, onClick, projectTitle, scrollY = 0, index, updateActiveBubble, modalOpen }) => {
  const meshRef = useRef();
  const materialRef = useRef();
  const initialPosition = useRef([...position]);
  const [hovered, setHovered] = useState(false);
  const [popping, setPopping] = useState(false);
  const popTimeoutRef = useRef(null);
  const [activeBubble] = useAtom(activeBubbleAtom);
  const [isAnyBubbleHovered, setIsAnyBubbleHovered] = useAtom(isAnyBubbleHoveredAtom);
  
  // Check if this bubble is the active one
  const isActive = activeBubble && activeBubble.index === index;
  

  // Track if bubble has been clicked (to stop video playback)
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  
  // Use drei's useVideoTexture hook for reliable video loading
  const texture = useVideoTexture(videoPath, {
    muted: true,
    loop: true,
    start: true,
    crossOrigin: 'anonymous',
    playsInline: true
  });
  
  // Configure texture properties for spherical mapping
  useEffect(() => {
    if (texture) {
      // Optimize texture mapping for spherical geometry
      texture.center = new THREE.Vector2(0.5, 0.5);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      
      // Calculate repeat value based on bubble size
      const baseRepeat = 1.4;
      const optimalRepeat = baseRepeat * (1 / size);
      texture.repeat = new THREE.Vector2(optimalRepeat, optimalRepeat);
      texture.offset = new THREE.Vector2(0.3, 0);
      texture.mapping = THREE.EquirectangularReflectionMapping;
      
      console.log("Video texture configured:", videoPath);
    }
  }, [texture, size, videoPath]);
  
  // Set up animation parameters
  const floatSpeed = useRef(Math.random() * 0.005 * speed);
  const startY = useRef(position[1]);
  const floatRange = useRef(Math.random() * 0.5 + 0.5);
  const timeOffset = useRef(Math.random() * 100);
  
  // Parallax effect strength - randomize slightly for each bubble
  const parallaxStrength = useRef(0.3 + Math.random() * 0.2);
  
  // Store the hover scale factor
  const hoverScaleRef = useRef(1.0);
  
  // Handle hover state and update activeBubble on hover
  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'auto';
    
    // Update global hover state
    setIsAnyBubbleHovered(hovered);
    
    // Update activeBubble when hovered
    if (hovered && updateActiveBubble) {
      updateActiveBubble({
        index,
        title: projectTitle
      });
    }
    
    return () => {
      document.body.style.cursor = 'auto';
    };
  }, [hovered, index, projectTitle, updateActiveBubble, setIsAnyBubbleHovered]);
  
  // Reset hover state when modal opens
  useEffect(() => {
    if (modalOpen && hovered) {
      setHovered(false);
    }
  }, [modalOpen, hovered]);
  
  // Handle pointer over event
  const handlePointerOver = () => {
    // Skip if modal is open
    if (modalOpen) return;
    
    setHovered(true);
    setIsAnyBubbleHovered(true);
    
    // Update activeBubble immediately on hover
    if (updateActiveBubble) {
      updateActiveBubble({
        index,
        title: projectTitle
      });
    }
  };
  
  // Handle pointer out event to check if we need to find a centered bubble
  const handlePointerOut = () => {
    // Skip if modal is open
    if (modalOpen) return;
    
    setHovered(false);
    setIsAnyBubbleHovered(false);
    
    // We'll let the useFrame function in each bubble detect which one is centered
    // The next frame will automatically update the activeBubble to the centered bubble (if any)
  };
  
  // Handle pop effect
  useEffect(() => {
    if (popping) {
      // Clear any existing timeout
      if (popTimeoutRef.current) {
        clearTimeout(popTimeoutRef.current);
      }
      
      // Reset popping state after animation duration
      popTimeoutRef.current = setTimeout(() => {
        setPopping(false);
      }, 300); // 300ms pop animation
    }
    
    return () => {
      if (popTimeoutRef.current) {
        clearTimeout(popTimeoutRef.current);
      }
    };
  }, [popping]);
  
  // Handle bubble click with pop effect and delayed callback
  const handleClick = (e) => {
    // Skip if modal is open
    if (modalOpen) return;
    
    setPopping(true);
    setHasBeenClicked(true);
    
    // Note: Video pause is now handled by useVideoTexture hook
    
    // Delay the onClick callback to allow time to see the pop effect
    if (onClick) {
      setTimeout(() => {
        onClick(e);
      }, 300); // 300ms delay matches the pop animation duration
    }
  };

  // Combined animation loop for position, scale, and material effects
  useFrame((state, delta) => {
    if (meshRef.current) {
      const time = state.clock.elapsedTime + timeOffset.current;
      
      // 1. Position animation (parallax and floating)
      // Apply cursor-based parallax effect
      const targetX = initialPosition.current[0] - cursor.x * parallaxStrength.current;
      const targetY = initialPosition.current[1] + cursor.y * parallaxStrength.current;
      const targetZ = initialPosition.current[2];
      
      // Add gentle floating motion on top of parallax
      const floatingY = Math.sin(time * floatSpeed.current) * floatRange.current;
      
      // Apply position changes with smooth easing
      meshRef.current.position.x += (targetX - meshRef.current.position.x) * 0.08;
      meshRef.current.position.y += ((targetY + floatingY) - meshRef.current.position.y) * 0.08;
      meshRef.current.position.z += (targetZ - meshRef.current.position.z) * 0.08;
      
      // Check if this bubble is in the center of the screen vertically
      // Only update based on center detection if no bubble is being hovered and modal is not open
      if (meshRef.current && updateActiveBubble && !isAnyBubbleHovered && !modalOpen) {
        // Get the bubble's position in screen space
        const screenPosition = new THREE.Vector3();
        screenPosition.copy(meshRef.current.position);
        screenPosition.project(state.camera);
        
        // Convert to normalized device coordinates (-1 to +1)
        const ndcY = screenPosition.y;
        
        // Check if this bubble is near the center of the screen vertically
        // We use a small threshold to determine "center"
        const centerThreshold = 0.2; // Adjust as needed
        const isInCenter = Math.abs(ndcY) < centerThreshold;
        
        // If this bubble is in the center, update the active bubble atom
        if (isInCenter) {
          updateActiveBubble({
            index,
            title: projectTitle
          });
        }
      }
      
      // 2. Scale animation (hover/active effect + subtle pulsing + scroll-based scaling)
      // Update scale with smooth transition based on hover and active states
      const targetHoverScale = (hovered || isActive) ? 1.9 : 1.0;
      hoverScaleRef.current += (targetHoverScale - hoverScaleRef.current) * ((hovered || isActive) ? 0.05 : 0.03);
      
      // Calculate scroll-based scale factor
      // Get the bubble's Y position in world space (negative values as we scroll down)
      const bubbleWorldY = initialPosition.current[1];
      
      // Calculate the scroll threshold where this bubble should be fully visible
      // This is based on the bubble's position in the scene
      const scrollThreshold = Math.abs(bubbleWorldY) * 550; // Adjust multiplier as needed
      
      // Calculate scroll progress for this specific bubble (0 to 1)
      const scrollProgress = Math.min(Math.max(scrollY / scrollThreshold, 0), 1);
      
      // Apply easing function to make the scale-up more natural
      const easedScrollProgress = scrollProgress === 1 ? 1 : 1 - Math.pow(1 - scrollProgress, 3);
      
      // Apply final scale (base size × hover scale × scroll progress)
      const finalScale = size * hoverScaleRef.current * easedScrollProgress;
      meshRef.current.scale.set(finalScale, finalScale, finalScale);
      
      // Apply z-position animation based on scroll progress
      // Start further back (negative z) and move forward as scroll progress increases
      const startZ = initialPosition.current[2] - 10; // Start 10 units back from original position
      const endZ = initialPosition.current[2]; // End at original position
      const currentZ = startZ + (endZ - startZ) * easedScrollProgress;
      
      // Update the z position with the same easing as used for scale
      meshRef.current.position.z = currentZ;
      
      // 3. Material animations
      if (materialRef.current) {
        // Animate distortion - reduced animation when not hovered/active, amplified when hovered/active
        const baseDistort = distortFactor;
        // Smaller sine wave amplitude for more stable appearance when not hovered/active
        const animatedDistort = baseDistort + Math.sin(time) * 0.05;
        // More amplification on hover/active (2.5x instead of 1.5x)
        const hoverDistort = (hovered || isActive) ? baseDistort * 2.5 : baseDistort;
        
        // Use consistent opacity approach - lower for inactive, max (0.7) for active/hovered
        const inactiveOpacity = 0.6; // Lower opacity for inactive bubbles
        const activeOpacity = 0.9;   // Maximum opacity for active/hovered bubbles
        const currentOpacity = (hovered || isActive) ? activeOpacity : inactiveOpacity;
        
        // Pop effect - extreme distortion when popping
        const popDistort = popping ? baseDistort * 5 : 0;
        const popOpacity = popping ? 0.2 : 0; // Lower opacity during pop effect
        
        materialRef.current.distort = THREE.MathUtils.lerp(
          materialRef.current.distort,
          popping ? popDistort : ((hovered || isActive) ? hoverDistort : animatedDistort),
          popping ? 0.8 : ((hovered || isActive) ? 0.15 : 0.1) // Very fast transition for pop effect
        );

        materialRef.current.opacity = THREE.MathUtils.lerp(
          materialRef.current.opacity,
          popping ? popOpacity : currentOpacity,
          popping ? 0.8 : ((hovered || isActive) ? 0.8 : 0.4)
        );
        
        // Enhance hover/active effect with material adjustments
        materialRef.current.clearcoat = THREE.MathUtils.lerp(
          materialRef.current.clearcoat,
          popping ? 10.0 : ((hovered || isActive) ? 4.5 : 1.0),
          popping ? 0.8 : 0.1
        );
        
        materialRef.current.envMapIntensity = THREE.MathUtils.lerp(
          materialRef.current.envMapIntensity,
          popping ? 10.0 : ((hovered || isActive) ? 6.5 : 1.0),
          popping ? 0.8 : 0.1
        );
        
        // Additional pop effects
        if (popping) {
          // Increase speed of distortion during pop
          materialRef.current.speed = THREE.MathUtils.lerp(
            materialRef.current.speed,
            5.0, // Much faster distortion during pop
            0.8
          );
        } else {
          // Return to normal speed
          materialRef.current.speed = THREE.MathUtils.lerp(
            materialRef.current.speed,
            1.5, // Normal speed
            0.1
          );
        }
      }
    }
  });

  return (
    <mesh 
      ref={meshRef} 
      position={position}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <sphereGeometry args={[size, 64, 64]} />
      <MeshDistortMaterial
        ref={materialRef}
        speed={1.5} // Slightly slower distortion speed
        distort={distortFactor}
        radius={1}
        envMapIntensity={2.0} // Increased from 1.2 for more brightness
        iridescence={1}
        iridescenceIOR={1}
        iridescenceThicknessRange={[0, 800]}
        clearcoat={3.0} // Increase for more shine
        clearcoatRoughness={0}
        metalness={0.2} 
        roughness={0.2}
        transparent={true}
        opacity={0.4} // Default opacity for inactive bubbles
        refractionRatio={0.5}
        map={texture}
        transmission={0.7} // Increased for more light transmission
      />
    </mesh>
  );
};


// Generate bubble configurations based on screen size and objectsDistance
const generateBubbleConfigs = (sizes, objectsDistance) => {
  // Calculate responsive scaling factor based on screen width
  // Add a minimum scale to prevent bubbles from becoming too small on mobile
  const rawScaleFactor = sizes.width / 1920;
  const minScaleFactor = 0.8; // Increased from 0.5 to ensure bubbles are larger on mobile
  const scaleFactor = Math.max(rawScaleFactor, minScaleFactor);
  
  // Create a separate position scaling factor for X-axis to bring bubbles closer on mobile
  // This factor will be smaller than scaleFactor on mobile to bring bubbles closer horizontally
  const isMobile = sizes.width < 768;
  const xPositionFactor = isMobile ? scaleFactor * 0.7 : scaleFactor; // Reduce X spread by 40% on mobile
  
  // Bubble positions and animation parameters
  const positions = [
    [-0.9 * xPositionFactor, -objectsDistance * 1.3, -1.0],
    [1.7 * xPositionFactor, -objectsDistance * 1.6, 2.0],
    [0.8 * xPositionFactor, -objectsDistance * 1.9, -1.8],
    [1.8 * xPositionFactor, -objectsDistance * 2.3, 4.0],
    [-1.3 * xPositionFactor, -objectsDistance * 2.6, 2.0],
    [0.85 * xPositionFactor, -objectsDistance * 2.9, 3.5],
    [3.3 * xPositionFactor, -objectsDistance * 3.2, -3.0],
    [-1.1 * xPositionFactor, -objectsDistance * 3.5, 2.0],
    [1.7 * xPositionFactor, -objectsDistance * 3.7, -3.0],
    [-0.3 * xPositionFactor, -objectsDistance * 4.1, 3.0]
  ];
  
  const speeds = [0.75, 0.8, 0.85, 0.9, 0.75, 0.87, 0.88];
  const distortFactors = [0.2, 0.22, 0.28, 0.16, 0.32, 0.35, 0.3];
  
  // Map selected projects to bubble configurations
  return projects.map((project, index) => ({
    position: positions[index % positions.length],
    size: scaleFactor,
    speed: speeds[index % speeds.length],
    distortFactor: distortFactors[index % distortFactors.length],
    videoPath: `/projects/${project.slug}_thumb.mp4`,
    project: project // Include the full project data
  }));
};

const Bubbles = ({ bubbleConfigs, scrollY, sizes, cursor, onBubbleClick, objectsDistance = 4, modalOpen }) => {
  const bubbleRefs = useRef([]);
  const { camera } = useThree();
  const [activeBubble, setActiveBubble] = useAtom(activeBubbleAtom);
  
  // Generate responsive bubble configurations if not provided
  const configs = useRef(bubbleConfigs || generateBubbleConfigs(sizes, objectsDistance));
  
  // Log video paths for debugging on mount
  useEffect(() => {
    console.log("Bubbles component mounted");
    configs.current.forEach((config, index) => {
      console.log(`Bubble ${index} video path:`, config.videoPath);
    });
    
    return () => {
      console.log("Bubbles component unmounted");
    };
  }, []);
  
  // Update bubble configurations when screen size changes
  useEffect(() => {
    if (!bubbleConfigs) {
      configs.current = generateBubbleConfigs(sizes, objectsDistance);
    }
  }, [sizes, objectsDistance, bubbleConfigs]);
  
  // Handle bubble click
  const handleBubbleClick = (index) => {
    // Skip if modal is open
    if (modalOpen) return;
    
    if (onBubbleClick) {
      onBubbleClick(index, configs.current[index]);
    } else {
      console.log(`Bubble ${index} clicked:`, configs.current[index]);
      // No default behavior - rely on parent component to handle click
    }
  };
  
  return (
    <group>
      {configs.current.map((config, i) => (
        <Bubble
          key={i}
          ref={(el) => (bubbleRefs.current[i] = el)}
          position={config.position}
          size={config.size}
          speed={config.speed}
          distortFactor={config.distortFactor}
          videoPath={config.videoPath}
          cursor={cursor}
          onClick={() => handleBubbleClick(i)}
          projectTitle={config.project.title}
          scrollY={scrollY}
          index={i}
          updateActiveBubble={setActiveBubble}
          modalOpen={modalOpen}
        />
      ))}
    </group>
  );
};

export default Bubbles;
